import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";

const BOOK_COVER = "/images/ebook-cover.jpg";
const AUTHOR_PHOTO = "/images/merls-headshot.jpg";

export default function BookClubKitPage() {
  const [form, setForm] = useState({ name: "", email: "", club: "", link: "", suburb: "", members: "", month: "", visit: "kit-only" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.club) {
      toast.error("Please fill in your name, email and book club name");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        message: `BOOK CLUB KIT REQUEST\nClub: ${form.club}\nWebsite/Social: ${form.link || 'Not provided'}\nSuburb: ${form.suburb}\nMembers: ${form.members}\nReading month: ${form.month}\nVisit preference: ${form.visit}`,
      });
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const faqs = [
    { q: "Does a visit from Merls cost anything?", a: "No. Visits are free for book clubs of 6 or more that are reading the book." },
    { q: "Where does Merls visit in person?", a: "Anywhere in South-East Queensland. Everywhere else in Australia, he'll join you on Zoom." },
    { q: "Is the book right for every club?", a: "It deals with alcohol, addiction and family pain, and it doesn't hold back. It's also funny, warm and full of heart. The kit includes a content note and support lines." },
    { q: "Where can we buy copies?", a: "At fyblife.com.au/store, with a group discount for 6+ copies, or through your local bookshop or library." },
    { q: "We're not a traditional book club. Can we still do this?", a: "Yes. Men's groups, workplace groups, recovery circles and a few mates at the pub all count." },
  ];

  return (
    <div className="bg-[#050505] min-h-screen font-body" data-testid="book-club-kit-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="font-brand text-2xl tracking-tighter text-white">FYB</Link>
          <a href="#signup" className="btn-gold px-5 py-2 text-xs tracking-[0.15em] uppercase rounded-sm">Get The Free Kit</a>
        </div>
      </nav>

      {/* HERO */}
      <header className="pt-28 pb-20 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold">For Book Clubs Across Australia</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase text-white leading-[1.02]">
              Bring Merls To Your Book Club
            </h1>
            <div className="gold-line-left w-20" />
            <p className="text-zinc-300 text-base sm:text-lg leading-relaxed max-w-lg">
              Forty years. One bender. The book your club won't stop talking about — and the author who'll pull up a chair.
            </p>
            <p className="text-zinc-500 text-sm">Free for clubs of 6+. In person in South-East QLD, Zoom anywhere in Australia.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#signup" className="btn-gold px-8 py-4 text-sm tracking-[0.15em] uppercase rounded-sm text-center">
                Get The Free Book Club Kit
              </a>
              <a href="#visit" className="border border-white/20 text-white px-8 py-4 text-sm tracking-[0.15em] uppercase rounded-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all text-center">
                How A Visit Works
              </a>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#D4AF37]/10 blur-3xl rounded-full" />
              <img src={BOOK_COVER} alt="The Forty Year Bender by Chris Merlo" className="relative w-64 sm:w-72 shadow-2xl shadow-black/50" />
            </div>
          </div>
        </div>
      </header>

      {/* BENEFITS */}
      <section className="py-20 px-6 bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-3">What Your Club Gets</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter uppercase text-white">Three Reasons To Say Yes</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { num: "01", title: "A Free Book Club Kit", desc: "12 discussion questions, a four-week reading plan and a letter from Merls to your club." },
              { num: "02", title: "A Visit From The Author", desc: "Merls joins your meeting in person or on Zoom. Ask him anything. Nothing's off limits." },
              { num: "03", title: "A Group Discount", desc: "Ordering 6 or more copies? You'll get a club code for your kit." },
            ].map((b) => (
              <div key={b.num} className="border-gold-glow bg-[#121212] p-8 rounded-sm space-y-4">
                <span className="font-heading text-4xl text-[#D4AF37]">{b.num}</span>
                <h3 className="font-heading text-xl tracking-tight uppercase text-white">{b.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-6">
            <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold">About The Book</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter uppercase text-white leading-[1.02]">
              Forty Years.<br />Told Straight.
            </h2>
            <div className="gold-line-left w-16" />
            <p className="text-zinc-300 text-base leading-relaxed">
              <em className="text-white">The Forty Year Bender</em> is Chris Merlo's memoir of four decades on the drink, and what it took to finally stop. It's honest, it's funny in places you won't expect, and some of it is hard to read.
            </p>
            <p className="text-zinc-400 text-base leading-relaxed">
              Everyone knows someone who has lived this. Most book clubs have never talked about it out loud. This book gives that conversation somewhere to start.
            </p>
            <p className="text-zinc-500 text-sm">
              Chris "Merls" Merlo is a Brisbane author, speaker and founder of FYB. The book launched on stage at La Boite Theatre in June 2026.
            </p>
          </div>
          <div className="space-y-6">
            <div className="relative">
              <img src={AUTHOR_PHOTO} alt="Chris Merls Merlo" className="w-full max-w-md rounded-sm border border-white/10 mx-auto" />
            </div>
            <blockquote className="border-l-2 border-[#D4AF37] pl-5 py-2">
              <p className="text-zinc-300 italic text-lg leading-relaxed">
                "I'll pull up a chair and you can ask me anything. Nothing's off limits. That's the whole point."
              </p>
              <cite className="text-[#D4AF37] text-sm font-semibold mt-2 block not-italic">— Merls</cite>
            </blockquote>
          </div>
        </div>
      </section>

      {/* HOW A VISIT WORKS */}
      <section id="visit" className="py-20 px-6 bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-3">How A Visit Works</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter uppercase text-white">30 To 45 Minutes With Merls</h2>
          </div>
          <div className="space-y-6">
            {[
              { time: "5 min", title: "Hello And A Reading", desc: "Your club picks a passage and Merls reads it." },
              { time: "30 min", title: "Ask Him Anything", desc: "Open Q&A, with the hardest questions first." },
              { time: "5 min", title: "Photo And Thank-You", desc: "A group photo and a signed copy for the club." },
            ].map((step) => (
              <div key={step.title} className="flex gap-6 items-start bg-[#121212] border border-white/10 p-6 rounded-sm">
                <div className="flex-shrink-0 w-16 text-center">
                  <span className="font-heading text-2xl text-[#D4AF37]">{step.time}</span>
                </div>
                <div>
                  <h3 className="font-heading text-lg tracking-tight uppercase text-white mb-1">{step.title}</h3>
                  <p className="text-zinc-400 text-sm">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROOF */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold">As Heard And Seen</p>
          <h2 className="font-heading text-3xl tracking-tighter uppercase text-white">Launched On Stage In Brisbane</h2>
          <div className="gold-line mx-auto" />
          <div className="flex flex-wrap justify-center gap-8">
            <div className="text-center">
              <p className="font-heading text-lg uppercase text-white">Book Launch</p>
              <p className="text-zinc-500 text-xs">La Boite Theatre, June 2026</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-lg uppercase text-white">Radio</p>
              <p className="text-zinc-500 text-xs">ABC Radio Brisbane</p>
            </div>
          </div>
        </div>
      </section>

      {/* SIGNUP FORM */}
      <section id="signup" className="py-20 px-6 bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left: Copy */}
            <div className="space-y-6">
              <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold">Free For Book Clubs</p>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter uppercase text-white leading-[1.02]">
                Get The Kit.<br />Book Merls.
              </h2>
              <p className="text-zinc-300 text-base leading-relaxed">
                Tell us about your club and we'll email the Book Club Kit straight away. If you'd like a visit, Merls' team will be in touch to lock in a date.
              </p>
              <ul className="space-y-3">
                {["12 discussion questions", "Four-week reading plan", "A letter from Merls to your club", "Group discount code for copies"].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-zinc-300 text-sm">
                    <ArrowRight className="w-3 h-3 text-[#D4AF37] flex-shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Form */}
            <div className="bg-[#121212] border border-white/10 p-8 rounded-sm">
              {!submitted ? (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="bookclub-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-heading text-xs tracking-[0.08em] uppercase text-zinc-400 mb-2">Your Name *</label>
                      <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-black border-white/20 focus:border-[#D4AF37] text-white placeholder:text-zinc-600 h-11" required data-testid="bookclub-name" />
                    </div>
                    <div>
                      <label className="block font-heading text-xs tracking-[0.08em] uppercase text-zinc-400 mb-2">Email *</label>
                      <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-black border-white/20 focus:border-[#D4AF37] text-white placeholder:text-zinc-600 h-11" required data-testid="bookclub-email" />
                    </div>
                  </div>
                  <div>
                    <label className="block font-heading text-xs tracking-[0.08em] uppercase text-zinc-400 mb-2">Book Club Name *</label>
                    <Input value={form.club} onChange={(e) => setForm({...form, club: e.target.value})} className="bg-black border-white/20 focus:border-[#D4AF37] text-white placeholder:text-zinc-600 h-11" required data-testid="bookclub-club" />
                  </div>
                  <div>
                    <label className="block font-heading text-xs tracking-[0.08em] uppercase text-zinc-400 mb-2">Website Or Social Media Link</label>
                    <Input value={form.link} onChange={(e) => setForm({...form, link: e.target.value})} placeholder="e.g. instagram.com/yourbookclub" className="bg-black border-white/20 focus:border-[#D4AF37] text-white placeholder:text-zinc-600 h-11" data-testid="bookclub-link" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-heading text-xs tracking-[0.08em] uppercase text-zinc-400 mb-2">Suburb And State</label>
                      <Input value={form.suburb} onChange={(e) => setForm({...form, suburb: e.target.value})} placeholder="e.g. Paddington, QLD" className="bg-black border-white/20 focus:border-[#D4AF37] text-white placeholder:text-zinc-600 h-11" data-testid="bookclub-suburb" />
                    </div>
                    <div>
                      <label className="block font-heading text-xs tracking-[0.08em] uppercase text-zinc-400 mb-2">Number Of Members</label>
                      <select value={form.members} onChange={(e) => setForm({...form, members: e.target.value})} className="w-full h-11 bg-black border border-white/20 focus:border-[#D4AF37] text-white px-3 rounded-sm text-sm" data-testid="bookclub-members">
                        <option value="">Choose</option>
                        <option value="Under 6">Under 6</option>
                        <option value="6 to 10">6 to 10</option>
                        <option value="11 to 20">11 to 20</option>
                        <option value="21+">21+</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block font-heading text-xs tracking-[0.08em] uppercase text-zinc-400 mb-2">When Would Your Club Read It?</label>
                    <select value={form.month} onChange={(e) => setForm({...form, month: e.target.value})} className="w-full h-11 bg-black border border-white/20 focus:border-[#D4AF37] text-white px-3 rounded-sm text-sm" data-testid="bookclub-month">
                      <option value="">Choose a month</option>
                      <option>December 2026</option>
                      <option>January 2027</option>
                      <option>February 2027</option>
                      <option>March 2027</option>
                      <option>Later in 2027</option>
                    </select>
                  </div>
                  <fieldset className="space-y-3">
                    <legend className="font-heading text-xs tracking-[0.08em] uppercase text-zinc-400 mb-2">Would You Like Merls To Visit?</legend>
                    {[
                      { value: "in-person", label: "In person" },
                      { value: "zoom", label: "On Zoom" },
                      { value: "kit-only", label: "Just the kit for now" },
                    ].map((opt) => (
                      <label key={opt.value} className="flex items-center gap-3 cursor-pointer group">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${form.visit === opt.value ? "border-[#D4AF37] bg-[#D4AF37]" : "border-white/30 group-hover:border-[#D4AF37]/50"}`}>
                          {form.visit === opt.value && <div className="w-2 h-2 rounded-full bg-[#050505]" />}
                        </div>
                        <input type="radio" name="visit" value={opt.value} checked={form.visit === opt.value} onChange={(e) => setForm({...form, visit: e.target.value})} className="sr-only" />
                        <span className="text-zinc-300 text-sm">{opt.label}</span>
                      </label>
                    ))}
                  </fieldset>
                  <Button type="submit" disabled={loading} className="w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase" data-testid="bookclub-submit">
                    {loading ? "Sending..." : "Send Me The Kit"}
                  </Button>
                  <p className="text-zinc-600 text-[10px] text-center">We'll only use your details to send the kit and arrange your visit.</p>
                </form>
              ) : (
                <div className="text-center py-12 space-y-6" data-testid="bookclub-success">
                  <h3 className="font-heading text-2xl tracking-tight uppercase text-white">Legend, Thank You.</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    Your Book Club Kit is ready. Download it below.
                  </p>
                  <a
                    href="/downloads/Forty_Year_Bender_Book_Club_Kit.pdf"
                    download="Forty_Year_Bender_Book_Club_Kit.pdf"
                    className="inline-flex items-center gap-3 btn-gold px-8 py-4 text-sm tracking-[0.1em] uppercase rounded-sm"
                    data-testid="bookclub-download"
                  >
                    <ArrowRight className="w-4 h-4" /> Download Book Club Kit (PDF)
                  </a>
                  <p className="text-zinc-600 text-xs">If you requested a visit, Merls' team will be in touch to lock in a date.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-3">Questions</p>
            <h2 className="font-heading text-3xl tracking-tighter uppercase text-white">Good To Know</h2>
            <div className="gold-line mx-auto mt-4" />
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-white/10 rounded-sm overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/[0.02] transition-colors"
                >
                  <span className="text-white text-sm font-medium pr-4">{faq.q}</span>
                  <ChevronDown className={`w-4 h-4 text-[#D4AF37] flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="text-zinc-400 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#050505] border-t border-white/10 py-16 px-6 text-center">
        <div className="max-w-4xl mx-auto space-y-4">
          <p className="font-heading text-3xl sm:text-4xl tracking-tighter uppercase text-white">Nothing's Off Limits.</p>
          <div className="space-y-1">
            <p className="text-zinc-500 text-sm">@chrismerlo.author</p>
            <a href="/" className="text-[#D4AF37] text-sm hover:underline">fyblife.com.au</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
